/**
 * Coachmark — reusable inline hint banner with persistent per-device
 * dismiss and optional DOM highlight of a target element.
 *
 * sl-8fwi.6 rework: The first audit pass built this behaviour inline
 * inside `execution-row-list.tsx` as a one-off banner with a hard-coded
 * emoji and no visual link to the element the user was supposed to
 * click. Deep-audit Run 1/2/3 flagged this on three fronts: (1) inline
 * emulation instead of a reusable component (AC2 / "0 Fallbacks"),
 * (2) hard-coded emoji violating the global "no emojis" convention,
 * (3) no visual pointer to the target element (AC3), (4) no auto
 * dismiss after a successful retry (AC4). This component fixes all
 * four in one place:
 *
 *   - Reusable React component with a small, explicit prop surface.
 *   - Uses InformationIcon from @blocksuite/icons/rc — no emoji.
 *   - Optional `highlightSelector`: while the coachmark is visible,
 *     every DOM element matching the selector gets the CSS class
 *     `coachmark-highlight-target` applied. The global style for that
 *     class lives in `./coachmark.css.ts` (pulse outline); the class
 *     is removed on dismiss/unmount/show-flip.
 *   - `autoDismissOn`: when the caller owns a boolean that flips to
 *     true (e.g. "all rows succeeded after retry"), the coachmark
 *     auto-dismisses and writes the persistent flag.
 *   - Persistent dismiss via localStorage, keyed by `storageKey` —
 *     the namespace is the caller's responsibility so two unrelated
 *     coachmarks can live side-by-side without colliding.
 *
 * Storage semantics:
 *   - Value `'1'` == dismissed. Any other value (or missing) == fresh.
 *   - Private-mode / quota-exceeded errors are swallowed so the UI
 *     never crashes on a storage write; in that case the coachmark
 *     behaves as "dismissed" to avoid blocking render with a hint
 *     that cannot be dismissed.
 */

// Side-effect import: vanilla-extract globalStyle for the highlight
// target class. Must be imported wherever the component ships so the
// CSS bundle includes the rule.
import './coachmark.css';

import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  streamLainCoachmarkInlineActionButtonClass,
  StreamLainCoachmarkMessage,
  StreamLainCoachmarkShell,
} from './streamlain-coachmark-shell';

/**
 * CSS class applied to every element matching `highlightSelector`
 * while the coachmark is visible. Exported so stylesheets can target
 * it with a predictable, collision-safe name.
 */
export const COACHMARK_HIGHLIGHT_CLASS = 'coachmark-highlight-target';

export interface CoachmarkProps {
  /** Short bold heading (already translated). */
  readonly title: ReactNode;
  /** Longer explanatory body (already translated). */
  readonly body: ReactNode;
  /** Label for the dismiss button (already translated). */
  readonly dismissLabel: ReactNode;
  /** Unique, namespaced key for the persistent dismiss flag. */
  readonly storageKey: string;
  /**
   * Show the coachmark when true. When false the banner is not
   * rendered and any highlight class is cleaned up immediately.
   */
  readonly show: boolean;
  /**
   * When the caller flips this flag to `true`, the coachmark
   * auto-dismisses. Intended for the "auto-dismiss after first
   * successful retry" AC — the caller computes whether the retry
   * actually resolved the previous failure and flips the flag.
   */
  readonly autoDismissOn?: boolean;
  /**
   * Optional CSS selector for DOM elements to highlight while the
   * coachmark is visible. Every matching element receives
   * `COACHMARK_HIGHLIGHT_CLASS`. The class is removed on dismiss,
   * unmount, or when `show` flips to false.
   */
  readonly highlightSelector?: string;
  /**
   * Called after persistent dismiss writes the flag. Useful for
   * parents that want to collapse the banner slot immediately or
   * track telemetry.
   */
  readonly onDismiss?: () => void;
  /** Optional testid for unit tests — defaults to 'coachmark'. */
  readonly testId?: string;
}

// ---------------------------------------------------------------------------
// Persistence helpers
// ---------------------------------------------------------------------------

function readDismissedFlag(storageKey: string): boolean {
  if (typeof window === 'undefined') return true; // SSR safety
  try {
    return window.localStorage.getItem(storageKey) === '1';
  } catch {
    // Private mode / quota exceeded: behave as "dismissed" so we never
    // block render with a hint that cannot be dismissed.
    return true;
  }
}

function writeDismissedFlag(storageKey: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, '1');
  } catch {
    // Best-effort — same rationale as the read path.
  }
}

// ---------------------------------------------------------------------------
// DOM highlight helpers
// ---------------------------------------------------------------------------

function applyHighlightClass(selector: string | undefined): void {
  if (!selector || typeof document === 'undefined') return;
  const matches = document.querySelectorAll(selector);
  matches.forEach(el => el.classList.add(COACHMARK_HIGHLIGHT_CLASS));
}

function removeHighlightClass(selector: string | undefined): void {
  if (!selector || typeof document === 'undefined') return;
  const matches = document.querySelectorAll(selector);
  matches.forEach(el => el.classList.remove(COACHMARK_HIGHLIGHT_CLASS));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Coachmark: FC<CoachmarkProps> = ({
  title,
  body,
  dismissLabel,
  storageKey,
  show,
  autoDismissOn,
  highlightSelector,
  onDismiss,
  testId = 'coachmark',
}) => {
  const [dismissed, setDismissed] = useState<boolean>(() =>
    readDismissedFlag(storageKey)
  );

  const handleDismiss = useCallback(() => {
    writeDismissedFlag(storageKey);
    setDismissed(true);
    onDismiss?.();
  }, [storageKey, onDismiss]);

  // Auto-dismiss when the caller signals the hint is no longer needed
  // (e.g. "all rows succeeded after retry" — AC4 of sl-8fwi.6 rework).
  useEffect(() => {
    if (!autoDismissOn || dismissed) return;
    writeDismissedFlag(storageKey);
    setDismissed(true);
    onDismiss?.();
  }, [autoDismissOn, dismissed, storageKey, onDismiss]);

  const visible = show && !dismissed;

  // Apply / remove the highlight class whenever the visible state or
  // the selector change. The cleanup path also runs on unmount.
  useEffect(() => {
    if (!highlightSelector) return undefined;
    if (visible) {
      applyHighlightClass(highlightSelector);
    } else {
      removeHighlightClass(highlightSelector);
    }
    return () => removeHighlightClass(highlightSelector);
  }, [visible, highlightSelector]);

  if (!visible) return null;

  return (
    <StreamLainCoachmarkShell
      compact
      role="status"
      style={{ marginBottom: 4 }}
      testId={testId}
      tone="warning"
    >
      <StreamLainCoachmarkMessage
        title={title}
        body={body}
        bodyTone="secondary"
        action={
          <button
            className={streamLainCoachmarkInlineActionButtonClass}
            data-testid={`${testId}-dismiss`}
            onClick={handleDismiss}
            type="button"
          >
            {dismissLabel}
          </button>
        }
      />
    </StreamLainCoachmarkShell>
  );
};
